import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { apiFetch } from '../lib/api';
import { blockToHtml, type Block } from '../lib/blocks';
import { SHARED_BLOCKS_KEY, useSharedBlocks } from '../lib/useSharedBlocks';
import { ApiError, type SharedBlock } from '../types/api';

type SharedRef = Extract<Block, { type: 'shared' }>;

/** Picks which saved block a "Shared block" block shows. Edits to the saved block reach every mail that uses it. */
export function SharedBlockPicker({
  block,
  onChange,
  fieldId,
}: {
  block: SharedRef;
  onChange: (next: Block) => void;
  fieldId: string;
}) {
  const { blocks, isLoading, isError } = useSharedBlocks();
  const missing =
    block.sharedBlockId !== '' && !isLoading && !blocks.some((b) => b.id === block.sharedBlockId);
  return (
    <div className="pf-field">
      <label className="pf-flabel" htmlFor={fieldId}>
        Shared block
      </label>
      <select
        id={fieldId}
        value={block.sharedBlockId}
        onChange={(e) => {
          const picked = blocks.find((b) => b.id === e.target.value);
          onChange({ ...block, sharedBlockId: picked?.id ?? '', name: picked?.name ?? '' });
        }}
      >
        <option value="">Choose a block…</option>
        {blocks.map((b) => (
          <option key={b.id} value={b.id}>
            {b.name}
          </option>
        ))}
      </select>
      {isError ? (
        <p className="pf-hint pf-hint-warn">The shared blocks could not be loaded.</p>
      ) : null}
      {missing ? (
        <p className="pf-hint pf-hint-warn">
          This shared block no longer exists. Pick another one.
        </p>
      ) : (
        <p className="pf-hint">
          Changes to the saved block reach every template and draft that uses it.{' '}
          <Link to="/indstillinger/delte-blokke">Manage shared blocks</Link>
        </p>
      )}
    </div>
  );
}

/** "Save as shared block" for an ordinary block: stores its HTML once and swaps the block for a reference. */
export function SaveAsSharedBlock({
  block,
  onReplace,
}: {
  block: Block;
  onReplace: (next: Block) => void;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const save = useMutation({
    mutationFn: () => {
      // Stored without its own show/device wrapper: those stay on the block in each mail.
      const plain: Block = { ...block };
      delete (plain as { show?: string }).show;
      delete (plain as { device?: string }).device;
      return apiFetch<SharedBlock>('/shared-blocks', {
        method: 'POST',
        body: { name: name.trim(), html: blockToHtml(plain), blockJson: JSON.stringify(block) },
      });
    },
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: SHARED_BLOCKS_KEY });
      onReplace({
        type: 'shared',
        id: block.id,
        sharedBlockId: created.id,
        name: created.name,
        ...('show' in block && block.show ? { show: block.show } : {}),
        ...('device' in block && block.device ? { device: block.device } : {}),
      });
      setOpen(false);
      setName('');
    },
  });
  const error =
    save.error instanceof ApiError
      ? (save.error.body.message ??
        (save.error.status === 409
          ? 'A shared block with this name already exists.'
          : 'It could not be saved.'))
      : save.error
        ? 'It could not be saved.'
        : null;

  if (!open) {
    return (
      <button type="button" className="pf-btn pf-btn-sm pf-btn-ghost" onClick={() => setOpen(true)}>
        Save as shared block
      </button>
    );
  }
  return (
    <span className="pf-field" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <input
        type="text"
        aria-label="Shared block name"
        placeholder="Name, e.g. Shipping info"
        maxLength={80}
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          // Enter saves and Escape cancels, like any small inline form.
          if (e.key === 'Enter') {
            e.preventDefault();
            if (name.trim() !== '' && !save.isPending) save.mutate();
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
      />
      <button
        type="button"
        className="pf-btn pf-btn-sm"
        disabled={name.trim() === '' || save.isPending}
        onClick={() => save.mutate()}
      >
        Save
      </button>
      <button
        type="button"
        className="pf-btn pf-btn-sm pf-btn-ghost"
        onClick={() => setOpen(false)}
      >
        Cancel
      </button>
      {error ? (
        <span role="alert" className="pf-hint pf-hint-warn">
          {error}
        </span>
      ) : null}
    </span>
  );
}
